import React from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import type { UserRole } from '@/types'

interface RequireAuthProps {
  children?: React.ReactNode
  roles?: UserRole[]
  redirectTo?: string
  showAccessDenied?: boolean
}

export function RequireAuth({
  children,
  roles,
  redirectTo = '/signup',
  showAccessDenied = false,
}: RequireAuthProps) {
  const { isAuthenticated, isLoading, hasRole } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-background"
        role="status"
        aria-label="Loading"
      >
        <div className="flex flex-col items-center gap-4">
          <div
            className="h-8 w-8 rounded-full border-2 border-oc-emerald border-t-transparent animate-spin"
            aria-hidden="true"
          />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname }}
        replace
      />
    )
  }

  if (roles && !hasRole(roles)) {
    if (!showAccessDenied) {
      return <Navigate to="/dashboard" replace />
    }

    return (
      <main className="mx-auto flex min-h-[60vh] w-full max-w-3xl flex-col items-center justify-center px-6 py-16 text-center">
        <h1 className="text-3xl font-semibold text-foreground">Access restricted</h1>
        <p className="mt-4 max-w-2xl text-base text-muted-foreground">
          Your account does not currently include access to this section. If your team requires this capability,
          submit a request through the contact form and include your tenant identifier and requested role scope.
        </p>
        <a
          href="/contact"
          className="mt-8 inline-flex items-center justify-center rounded-md bg-oc-emerald px-5 py-3 text-sm font-medium text-white hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-oc-emerald"
          data-testid="auth-link-request-access"
        >
          Request access
        </a>
      </main>
    )
  }

  return <>{children ?? <Outlet />}</>
}
